import { jaModel } from "./budoux-ja-model";
import { KEEP_WORDS } from "./nowrap-units";

/**
 * 日本語の題を語の境目 (文節) で分ける。BudouX の parser.js と同じ式で、純粋な JS なので SSR でも動く。
 * 折れの位置は題の文字列から決まるので、サーバーと手元で同じ結果になる
 */
const GROUPS = ["UW1", "UW2", "UW3", "UW4", "UW5", "UW6", "BW1", "BW2", "BW3", "TW1", "TW2", "TW3", "TW4"] as const;
const BASE_SCORE =
  -0.5 * Object.values(jaModel).reduce((sum, group) => sum + Object.values(group).reduce((s, v) => s + v, 0), 0);

export function score(sentence: string, i: number): number {
  const at = (group: (typeof GROUPS)[number], from: number, to: number) => jaModel[group]?.[sentence.substring(from, to)] ?? 0;
  return (
    BASE_SCORE +
    at("UW1", i - 3, i - 2) + at("UW2", i - 2, i - 1) + at("UW3", i - 1, i) +
    at("UW4", i, i + 1) + at("UW5", i + 1, i + 2) + at("UW6", i + 2, i + 3) +
    at("BW1", i - 2, i) + at("BW2", i - 1, i + 1) + at("BW3", i, i + 2) +
    at("TW1", i - 3, i) + at("TW2", i - 2, i + 1) + at("TW3", i - 1, i + 2) + at("TW4", i, i + 3)
  );
}

/** 数と単位 (18ヶ月、3か月) の途中。ここでは割らない */
const NUMBER_UNIT = /[0-9０-９][ヶケか]?$/;
/** 数の範囲 (問 1〜5、第 2〜4 章) の途中。「1〜」と「5」に離れて 2 行になった (10/22 道場の題) */
const DIGIT = /[0-9０-９]/;
const RANGE_MARK = /[〜～~]/;
const insideNumberRange = (text: string, i: number): boolean =>
  (RANGE_MARK.test(text[i - 1] ?? "") && DIGIT.test(text[i - 2] ?? "") && DIGIT.test(text[i] ?? "")) ||
  (RANGE_MARK.test(text[i] ?? "") && DIGIT.test(text[i - 1] ?? "") && DIGIT.test(text[i + 1] ?? ""));
const KATAKANA = /[\u30A1-\u30FA\u30FC]/;
const ASCII_WORD = /[A-Za-z0-9&+#.]/;
/**
 * カタカナ語のすぐ後ろに付いて 1 つの語を作る漢字 1 字 (カルノー図、カンパニー制、シナリオ法、データ型、ネットワーク層)。
 * BudouX はこの境目で割るので、狭い札で「カルノー / 図と論理式」と語の途中で折れた (10/25 資格の過去問の題)
 */
const KANJI_SUFFIX = /[図表法制型式性率値者層網側版系]/;
const KANJI = /[\u3400-\u9FFF々]/;

/** 後ろで折ってよい区切りの字。BudouX は「・」「＆」「：」「】」「／」の後ろと開き括弧の前をほとんど割らず、「オーバーロード・型変換・例外処理の」のような長い 1 文節ができて、行より長いと語の途中で折れる */
const DELIMITER = /[・＆：】／]/;
/** 前で折ってよい字。開き括弧は直前の字が開き括弧や空白でなければ、前を文節の境目にする */
const OPENING = /[【「『（〔［]/;
/** 行頭に置けない字。区切りの後ろにこれが続くなら割らない */
const NO_LINE_START = /^[、。，．,.)）\]］}｝」』】〕〉》"'”’ーぁぃぅぇぉっゃゅょゎァィゥェォッャュョヮヵヶ!！?？:：;；・\s]/;

/** 長いカタカナ語 (KEEP_WORDS) が 2 つ続く所の境目。「セキュリティ|インシデント」のように、語と語の間だけ割る */
function keepWordBoundaries(text: string): Set<number> {
  const out = new Set<number>();
  const ends = new Set<number>();
  const starts = new Set<number>();
  for (const m of text.matchAll(new RegExp(KEEP_WORDS.source, "g"))) {
    starts.add(m.index);
    ends.add(m.index + m[0].length);
  }
  for (const e of ends) if (starts.has(e)) out.add(e);
  return out;
}

/** BudouX はカタカナの連なりと漢字の間をほとんど割らないので、カタカナと漢字の境目だけ足す (ひらがな、英字の前後は足さない) */
function isScriptBoundary(prev: string, next: string): boolean {
  return (KATAKANA.test(prev) && KANJI.test(next)) || (KANJI.test(prev) && KATAKANA.test(next));
}

/**
 * カタカナ語と英数字の語の中。BudouX はカタカナ語の中でも割る (「ネット / ワーク」「プログラ / ミング」「プロジェクトマ / ネジメント」、10/22 教科書の札の題)。
 * 語の中では割らず、語が行より長いときだけ .lg-phrase の overflow-wrap: anywhere で折る
 */
function insideWord(prev: string, next: string): boolean {
  return (KATAKANA.test(prev) && KATAKANA.test(next)) || (ASCII_WORD.test(prev) && ASCII_WORD.test(next));
}

/** 尾を前の文節に付けたあと、この字数以上の文節は、尾の前の境目を残す (狭い札の 1 行に入らない長さ。「プレゼンテーションの基礎」の 12 字で「基 / 礎」と折れた) */
const LONG_PHRASE = 12;

/** 英数字の連なりがこの字数以上なら、すぐ後ろのカタカナ語の前で割ってよい。「FDE&ITコンサル」が 1 文節だと狭い札で「コンサ / ル」と語の途中で折れる */
const ASCII_RUN_BEFORE_KATAKANA = 3;
const asciiRunEndingAt = (text: string, end: number): number => {
  let n = 0;
  while (n < end && ASCII_WORD.test(text[end - 1 - n])) n++;
  return n;
};

/** 最後の文節がこの字数以下だと、折れたときに 1〜2 字だけの行になる */
const SHORT_TAIL = 2;
/**
 * 漢字とカタカナの境目で BudouX が割るが、1 つの語として読む物。
 * 教科書の章のミニテストの選択肢で「作業 / ディレクトリ」「回帰 / テスト」と割れた語 (10/21) も入れる。長い方を先に書く
 */
const NO_SPLIT_WORDS = new RegExp(
  [
    // サービス名 (ゆめさくコード、ゆめさくエンジニア)。「ゆめさく」はひらがな、後ろはカタカナで isScriptBoundary の対象外だが、
    // BudouX のスコアで割れる (10/25 /about の題が「ゆめさく / コードについて」に折れた)
    "ゆめさくコード", "ゆめさくエンジニア",
    "学習パス", "演習パス", "Web(?:ページ|サイト|アプリ)",
    "作業ディレクトリ", "(?:単体|結合|統合|回帰|自動)テスト", "(?:文字|改行|状態)コード", "(?:不正|同時)アクセス", "設定ファイル",
    "(?:基幹系|情報系|情報|管理|全銀|予約)システム", "(?:仮想|物理)マシン", "(?:仮想|公開)サーバー", "修正プログラム", "(?:二重|無限)ループ",
    "実行時エラー", "標準エラー", "情報セキュリティ", "継続的インテグレーション", "仮想デスクトップ", "検索エンジン", "内部ネットワーク",
    "気にな[るりっら]", "気にし[てたな]", "気にせ",
    "抽象クラス", "楽観ロック", "選択ソート", "潜在ニーズ", "住宅ローン", "日銀ネット", "確証バイアス", "責任共有モデル",
  ].join("|"),
);
/** 手前の文節をさらに割るには、この字数が要る */
const SPLITTABLE = 6;

/**
 * 文節に分ける。数と単位は離さず、1 字だけの文節は前に付ける。
 * 最後の文節が 2 字以下のときは、1〜2 字だけの行を作らないよう、手前の文節の中で BudouX の点がいちばん高い所でもう 1 度割り
 * (コンピュータ / サイエンス基礎)、割れない短さなら手前に付ける
 */
export function phraseSegments(text: string): string[] {
  if (text.length < 2) return [text];
  const cuts: number[] = [];
  const wordBounds = keepWordBoundaries(text);
  for (let i = 1; i < text.length; i++) {
    if (NUMBER_UNIT.test(text.slice(Math.max(0, i - 2), i))) continue;
    if (insideNumberRange(text, i)) continue;
    // 英数字の連なり (「&OS」「C++」「Node.js」) の途中では割らない。「アーキテクチャ& / OS」と折れた
    if (ASCII_WORD.test(text[i - 1]) && ASCII_WORD.test(text[i])) continue;
    // 英字とハイフンの境目 (「LPIC-1」の「-」の前後) でも割らない
    if ((text[i - 1] === "-" && /[A-Za-z]/.test(text[i - 2] ?? "")) || (text[i] === "-" && /[A-Za-z]/.test(text[i - 1]))) continue;
    const delimiter =
      (DELIMITER.test(text[i - 1]) && !NO_LINE_START.test(text[i])) || (OPENING.test(text[i]) && !OPENING.test(text[i - 1]) && !/\s/.test(text[i - 1]));
    // カタカナ語の中は、長いカタカナ語が 2 つ続く所 (セキュリティ|インシデント) のほかは割らない
    if (insideWord(text[i - 1], text[i]) && !wordBounds.has(i)) continue;
    if (KATAKANA.test(text[i - 1]) && KANJI_SUFFIX.test(text[i])) continue;
    if (score(text, i) > 0 || isScriptBoundary(text[i - 1], text[i]) || (KATAKANA.test(text[i]) && asciiRunEndingAt(text, i) >= ASCII_RUN_BEFORE_KATAKANA) || delimiter || wordBounds.has(i)) {
      // 空白 (折らない空白 U+00A0 も) の手前で割ると、次の行の頭に空白が残る (11/4 /joho1/dncl の帯の題「表記を / Python で」)。
      // 割る所を空白 (続く空白も) の後ろへずらし、空白は前の行の終わりに付ける
      let c = i;
      while (c < text.length && /\s/.test(text[c])) c++;
      if (cuts[cuts.length - 1] !== c && c < text.length) cuts.push(c);
    }
  }
  // 1 つの語として読む語 (学習パス、演習パス) の中では割らない。「基本エンジニア学習 / パスの進め方」と折れた (10/19 Fable の見直しの 14)
  const inside = new Set<number>();
  // 長いカタカナ語 (KEEP_WORDS) の中でも割らない。BudouX が「フル / スタックで実装」と割った (10/19 実践プロジェクトの札)
  for (const re of [NO_SPLIT_WORDS, KEEP_WORDS]) {
    for (const m of text.matchAll(new RegExp(re.source, "g"))) {
      for (let i = (m.index ?? 0) + 1; i < (m.index ?? 0) + m[0].length; i++) inside.add(i);
    }
  }
  // 1 字だけの文節は前に付ける (先頭の 1 字は後ろに付く)
  let bounds: number[] = [];
  for (const c of cuts.filter((c) => !inside.has(c))) {
    if (c === 1 || c - (bounds[bounds.length - 1] ?? 0) === 1 || text.length - c === 1) continue;
    bounds.push(c);
  }
  const last = bounds[bounds.length - 1];
  if (last !== undefined && text.length - last <= SHORT_TAIL) {
    const start = bounds[bounds.length - 2] ?? 0;
    bounds = bounds.slice(0, -1);
    if (last - start >= SPLITTABLE) {
      const afterDelimiter = DELIMITER.test(text[last - 1]);
      let best = -1;
      let bestScore = -Infinity;
      for (let i = start + 2; i <= last - 2; i++) {
        if (NUMBER_UNIT.test(text.slice(i - 2, i)) || insideNumberRange(text, i) || inside.has(i) || insideWord(text[i - 1], text[i]) || (KATAKANA.test(text[i - 1]) && KANJI_SUFFIX.test(text[i])) || /\s/.test(text[i - 1] + text[i])) continue;
        // 区切りの字の後ろが短いだけのときは、手前のカタカナ語の内側に折れ目を作らない (データベース・基本)
        if (afterDelimiter && KATAKANA.test(text[i - 1]) && KATAKANA.test(text[i])) continue;
        const sc = score(text, i);
        if (sc > bestScore) {
          bestScore = sc;
          best = i;
        }
      }
      if (best > 0) bounds.push(best);
      // ほかに割れる所が無く、残る文節が長い (前が 1 つのカタカナ語の「プロジェクトマネジメント入門」) ときは、短い尾の前の境目を残す。
      // 残さないと、文節が行より長い狭い札で「入 / 門」と語の途中で折れる
      else if (text.length - start >= LONG_PHRASE) bounds.push(last);
    }
  }
  const out: string[] = [];
  let from = 0;
  for (const b of bounds) {
    out.push(text.slice(from, b));
    from = b;
  }
  out.push(text.slice(from));
  return out;
}

/** 字の幅の見積り。全角を 1、半角を 0.5 で数える */
export function widthUnits(text: string): number {
  let w = 0;
  for (const ch of text) w += ch.charCodeAt(0) < 0x250 ? 0.5 : 1;
  return w;
}

/**
 * 1 行に収めたい名前を、文節の境目で切って「…」を付ける。語の途中では切らない。
 * 最初の文節だけで幅を超えるときは、その文節を丸ごと残す (字の途中で切るより、表示側の省略に任せる)
 */
export function clipAtPhrase(text: string, maxUnits: number): string {
  if (widthUnits(text) <= maxUnits) return text;
  let out = "";
  for (const seg of phraseSegments(text)) {
    if (out !== "" && widthUnits(out + seg) > maxUnits) break;
    out += seg;
  }
  return `${out.replace(/[\s、。・]+$/, "")}…`;
}
